// src/shared/components/ui/Avatar/UserAvatar.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../../styles/theme/colors';

interface UserAvatarProps {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  userRole?: 'traveller' | 'sender' | 'receiver' | '';
  size?: number;
  fontSize?: string;
  verified?: boolean;
  verifiedIcon?: React.ReactNode;
  alt?: string;
  borderWidth?: number;
  showBorder?: boolean;
  /** Use the chat participant badge palette (distinct sender/traveller/receiver colors) instead of the default theme primary colors */
  useChatBadgeColors?: boolean;
}

/**
 * UserAvatar Component
 *
 * Displays user avatar with the following features:
 * - Shows profile image if available
 * - Shows user initials in primary color if no image
 * - Adapts colors based on user role (traveller/sender)
 * - Supports verified badge
 * - Fully responsive with customizable size
 *
 * @example
 * <UserAvatar
 *   src={user.profile_image}
 *   name={user.full_name}
 *   userRole={user.user_role}
 *   size={64}
 * />
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  name,
  email,
  userRole = 'traveller',
  size = 64,
  fontSize,
  verified = false,
  verifiedIcon,
  alt,
  borderWidth = 0,
  showBorder = false,
  useChatBadgeColors = false,
}) => {
  // State to track if image failed to load
  const [imageError, setImageError] = React.useState(false);

  // Get primary color based on user role
  const primaryColor = useChatBadgeColors
    ? userRole === 'sender'
      ? colors.chatAvatar.sender.background
      : userRole === 'receiver'
        ? colors.chatAvatar.receiver.background
        : colors.chatAvatar.traveller.background
    : userRole === 'sender'
      ? colors.sender.primary
      : colors.traveller.primary;

  const initialsColor = useChatBadgeColors
    ? userRole === 'sender'
      ? colors.chatAvatar.sender.text
      : userRole === 'receiver'
        ? colors.chatAvatar.receiver.text
        : colors.chatAvatar.traveller.text
    : '#FFFFFF';

  // Function to get user initials
  const getInitials = (fullName?: string | null, userEmail?: string | null): string => {
    // If name is available, use it
    if (fullName && typeof fullName === 'string' && fullName.trim()) {
      const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);

      if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
      }

      if (nameParts.length >= 2) {
        // First name initial + Last name initial
        const firstInitial = nameParts[0].charAt(0).toUpperCase();
        const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
        return `${firstInitial}${lastInitial}`;
      }
    }

    // For new users without a name, extract initials from email
    if (userEmail && typeof userEmail === 'string') {
      const emailPrefix = userEmail.split('@')[0];
      // Remove numbers and special characters, get first 2 letters
      const letters = emailPrefix.replace(/[^a-zA-Z]/g, '');
      if (letters.length >= 2) {
        return letters.substring(0, 2).toUpperCase();
      }
      if (letters.length === 1) {
        return letters.toUpperCase();
      }
    }

    // Fallback
    return 'U';
  };

  // Reset image error state when src changes
  React.useEffect(() => {
    setImageError(false);
  }, [src]);

  // Calculate font size based on avatar size
  const calculatedFontSize = fontSize || `${Math.floor(size * 0.4)}px`;

  // Calculate verified icon size (about 20-25% of avatar size)
  const verifiedIconSize = Math.floor(size * 0.22);

  // Determine if we should show the image or initials
  const showImage = src && !imageError;

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${size}px`,
        height: `${size}px`,
        flexShrink: 0,
      }}
    >
      {/* Avatar Container */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          overflow: 'hidden',
          border: showBorder ? `${borderWidth || 2}px solid #FFFFFF` : 'none',
          boxShadow: size >= 100
            ? '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03)'
            : '0px 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        {showImage ? (
          // Show profile image if available and loaded successfully
          <Box
            component="img"
            src={src}
            alt={alt || name || 'User avatar'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.src.includes('?t=')) {
                img.src = src + '?t=' + Date.now();
              } else {
                setImageError(true);
              }
            }}
          />
        ) : (
          // Show initials with primary color background if no image or image failed
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: primaryColor,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: calculatedFontSize,
                lineHeight: 1,
                color: initialsColor,
                userSelect: 'none',
              }}
            >
              {getInitials(name, email)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Contrast border overlay */}
      <Box
        sx={{
          boxSizing: 'border-box',
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      {/* Verified badge */}
      {verified && (
        <Box
          sx={{
            position: 'absolute',
            width: `${verifiedIconSize}px`,
            height: `${verifiedIconSize}px`,
            right: size >= 100 ? '7px' : '2px',
            bottom: size >= 100 ? '5px' : '0px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {verifiedIcon}
        </Box>
      )}
    </Box>
  );
};

export default UserAvatar;
